import ScrollModal from "@/components/common/scroll-modal";
import {
  Heading,
  Button,
  Icon,
  GroupTextFieldRow,
  GroupTextField,
  Input,
  useToast,
  Avatar,
  AvatarFallback,
} from "@elaniintc/goes-design-system";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import type { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextAreaField } from "@/components/common/text-field";
import { newTableSchema, type TableData } from "@/schemas/data-center";
import { useCreateTable } from "@/queries/data-center/data-center.api";
import { useNavigate } from "react-router";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { OptionTooltip } from "@/workflow/components/configurations/form/components/OptionTooltip";
import { useEffect, useState } from "react";

interface NewTableModal {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const initialState: TableData = {
  name: "",
  description: "",
  table_display_name: "",
};

export default function NewTableModal({
  open,
  onClose,
  onSuccess,
}: NewTableModal) {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setError,
    clearErrors,
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(newTableSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: initialState,
  });
  const { mutateAsync, isPending: isLoadingCreateTable } = useCreateTable();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { hasPermission } = useAuthorization();
  const [locked, setLocked] = useState(true);
  const canReadRegistry = hasPermission(
    Permissions["data-center"]["read-registry"],
  );
  const onSubmit = async (data: TableData) => {
    try {
      const response = await mutateAsync({
        name: data.name,
        table_display_name: data.table_display_name,
        ...(data.description && { description: data.description }),
      });
      if (response.data.id && canReadRegistry) {
        navigate(`/centro-de-datos/${response.data.id}`);
      }

      showToast({
        title: "Guardado Satisfactorio",
        description: "La tabla fue creada con éxito",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square" type="icon">
                <Icon type="fas" name="save" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });

      clearErrors();
      handleClose();
      onSuccess();
    } catch (e) {
      const error = e as AxiosError<{
        message?: string;
        errors?: { [key: string]: string[] };
      }>;

      if (error.response?.data.message?.includes("already exists")) {
        setError("name", {
          type: "server",
          message: "Ya existe una tabla con este nombre",
        });
        return;
      }

      setError("name", {
        type: "server",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Error al crear la tabla",
      });
    }
  };

  const handleClose = () => {
    reset({
      table_display_name: "",
      description: "",
      name: "",
    });
    onClose();
  };

  const handleLockToggle = () => {
    setLocked((l) => !l);
  };

  const nameValue = watch("table_display_name");

  useEffect(() => {
    if (nameValue && locked) {
      const generatedKey = nameValue
        .toLocaleLowerCase()
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "_");

      setValue("name", generatedKey, { shouldValidate: true });
    }
  }, [nameValue, setValue, locked]);

  return (
    <ScrollModal
      isOpen={open}
      onClose={handleClose}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <DialogTitle className="sr-only">Nueva Tabla</DialogTitle>
          <DialogDescription className="sr-only">
            Formulario para crear una nueva tabla de datos
          </DialogDescription>
          <Heading variant="h5" className="mb-0!">
            Nueva Tabla
          </Heading>
          <Button
            type="button"
            onClick={handleClose}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" />
          </Button>
        </div>
      }
      footer={
        <div className="w-full grid grid-cols-1 tablet:grid-cols-2 py-3 px-8 gap-3">
          <Button
            variant="tertiary"
            size="sm"
            type="button"
            onClick={handleClose}
            className="min-w-30 cursor-pointer"
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="sm"
            type="submit"
            disabled={!isValid || isLoadingCreateTable}
            form="new-table-form"
            className="min-w-30 cursor-pointer"
          >
            Crear Tabla
            {isLoadingCreateTable && (
              <Icon type="fas" name="spinner-third" className="animate-spin" />
            )}
          </Button>
        </div>
      }
    >
      <form
        id="new-table-form"
        data-testid="new-table-form"
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col flex-1 min-h-0 px-4 sm:px-8"
      >
        {/* Contenido scrollable */}
        <div className="flex flex-col gap-6 pb-6">
          {/* Detalles */}
          <div>
            <Heading variant="h6" className="mb-4">
              Detalles
            </Heading>
            <div className="flex flex-col gap-4">
              <GroupTextField
                label="Nombre de la tabla"
                required
                error={
                  errors.table_display_name?.message ||
                  errors.name?.message ||
                  ""
                }
              >
                <GroupTextFieldRow>
                  <Input
                    className="w-full"
                    required
                    maxLength={100}
                    placeholder="Escribe un nombre para la tabla"
                    {...register("table_display_name")}
                  />
                </GroupTextFieldRow>
                <GroupTextFieldRow>
                  <div className="flex items-center w-full relative">
                    <Input
                      className="w-full pr-12"
                      placeholder="key"
                      disabled={locked}
                      group="down-left"
                      {...register("name")}
                    />
                    <OptionTooltip title="La key es el identificador técnico del campo. Se recomienda no modificarla si ya se ha utilizado en integraciones o automatizaciones.">
                      <div className="absolute right-4 top-3 text-body">
                        <Icon name="circle-info" type="far" />
                      </div>
                    </OptionTooltip>
                  </div>

                  <Button
                    variant="quiet"
                    type="button"
                    onClick={handleLockToggle}
                    aria-label={
                      locked
                        ? "Desbloquear edición de key"
                        : "Bloquear edición de key"
                    }
                  >
                    <Icon name={locked ? "lock" : "lock-open"} type="far" />
                  </Button>
                </GroupTextFieldRow>
              </GroupTextField>

              <TextAreaField
                label="Descripción"
                helperText="Opcional"
                maxLength={255}
                placeholder="Describe el contenido de tu tabla"
                rows={3}
                className="resize-none w-full"
                error={errors.description?.message || ""}
                {...register("description")}
              />
            </div>
          </div>
        </div>
      </form>
    </ScrollModal>
  );
}
